import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog';

import { takeUntil } from "rxjs/operators";
import { Subject, ReplaySubject } from 'rxjs';

import { Country } from '@sparro/app/core/models/location.model';
import { Tag, Discipline } from '@sparro/app/core/models/supertag.model';

@Component({
  selector: 'app-filter-sort-dialog',
  templateUrl: './filter-sort-dialog.component.html',
  styleUrls: ['./filter-sort-dialog.component.scss']
})

export class FilterSortDialogComponent implements OnInit, OnDestroy {
  order = [];
  timeline = [];
  show = [];
  locations: Country[] = [];
  disciplines: Discipline[] = [];
  tags: Tag[] = [];

  isAll: boolean = true;
  isPrevious: boolean = false;
  isUpcoming: boolean = false;

  selectedDisciplines: Discipline[] = [];
  selectedLocations: Country[] = [];
  selectedTags: Tag[] = [];

  selectedShowType: string;
  selectedOrder: string;
  selectedTimeline: string;

  disciplineArray: number[] = [];
  locationArray: string[] = [];
  tagArray: number[] = [];

  is_loading: boolean = false;
  selectable: boolean = true;
  removable: boolean = true;
  editorial: boolean = false;
  registrationStartDate: string = '';
  registrationEndDate: string = '';
  submissionStartDate: string = '';
  submissionEndDate: string = '';
  filter: boolean = false;
  sort: boolean = false;
  registrationPlaceholder: string = 'Registration date';
  submissionPlaceholder: string = 'Submission date';

  public locationControl: FormControl = new FormControl();
  public disciplineControl: FormControl = new FormControl();
  public tagControl: FormControl = new FormControl();
  public organizationControl: FormControl = new FormControl("");

  public locationSearchControl: FormControl = new FormControl();
  public disciplineSearchControl: FormControl = new FormControl();
  public tagSearchControl: FormControl = new FormControl();

  public searchedLocations: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  public searchedDisciplines: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  public searchedTags: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);

  private _onDestroy = new Subject<void>();
  registrationDateRange: string = '';
  submissionDateRange: string = '';
  selectedRegistrationDateRange: string = '';
  selectedSubmissionDateRange: string = '';

  constructor(
    public dialogRef: MatDialogRef<FilterSortDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) { }

  ngOnInit() {
    this.is_loading = true;
    if (this.data) {
      this.filter = this.data.filter;
      this.sort = this.data.sort;
      this.order = this.data.order;
      this.timeline = this.data.timeline;
      this.show = this.data.show;
      this.disciplines = this.data.disciplines;
      this.tags = this.data.tags;
      this.locations = this.data.locations;

      this.selectedOrder = this.data.selectedOrder;
      this.selectedTimeline = this.data.selectedTimeline;
      this.selectedShowType = this.data.selectedShowType;

      this.selectedDisciplines = [...this.data.selectedDisciplines];
      this.selectedLocations = [...this.data.selectedLocations];
      this.selectedTags = [...this.data.selectedTags];
      this.registrationDateRange = this.data.registrationDateRange;
      this.submissionDateRange = this.data.submissionDateRange;
      this.selectedRegistrationDateRange = this.data.selectedRegistrationDateRange;
      this.selectedSubmissionDateRange = this.data.selectedSubmissionDateRange;
      this.registrationStartDate = this.data.registrationStartDate;
      this.registrationEndDate = this.data.registrationEndDate;
      this.submissionStartDate = this.data.submissionStartDate;
      this.submissionEndDate = this.data.submissionEndDate;
      this.organizationControl.setValue(this.data.organizationControl);
      this.isAll = this.data.isAll;
      this.isPrevious = this.data.isPrevious;
      this.isUpcoming = this.data.isUpcoming;

      this.disciplineArray = [...this.data.disciplineArray];
      this.locationArray = [...this.data.locationArray];
      this.tagArray = [...this.data.tagArray];
      this.editorial = this.data.editorial
    }

    this.is_loading = false;
    this.initSearchControls();
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  initSearchControls() {
    if (this.locations) {
      if (this.selectedDisciplines.length > 0) {
        this.locationControl.setValue(this.selectedLocations);
      } else {
        this.locationControl.setValue(null);
      }
      this.searchedLocations.next(this.locations.slice());
      this.locationSearchControl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterLocations();
      });
    }
    if (this.disciplines) {
      if (this.selectedDisciplines.length > 0) {
        this.disciplineControl.setValue(this.selectedDisciplines);
      } else {
        this.disciplineControl.setValue(null);
      }
      this.searchedDisciplines.next(this.disciplines.slice());
      this.disciplineSearchControl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterDisciplines();
      });
    }
    if (this.tags) {
      if (this.selectedTags.length > 0) {
        this.tagControl.setValue(this.selectedTags);
      } else {
        this.tagControl.setValue(null);
      }
      this.searchedTags.next(this.tags.slice());
      this.tagSearchControl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterTags();
      });
    }
  }

  filterDisciplines() {
    if (!this.disciplines) {
      return;
    }
    let search = this.disciplineSearchControl.value;
    if (!search) {
      this.searchedDisciplines.next(this.disciplines.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    this.searchedDisciplines.next(
      this.disciplines.filter(discipline => discipline.name.toLowerCase().indexOf(search) > -1)
    );
  }

  filterLocations() {
    if (!this.locations) {
      return;
    }
    let search = this.locationSearchControl.value;
    if (!search) {
      this.searchedLocations.next(this.locations.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    this.searchedLocations.next(
      this.locations.filter(location => location.name.toLowerCase().indexOf(search) > -1)
    );
  }

  setEditorial(event) {
    this.editorial = event.checked;
  }

  filterTags() {
    if (!this.tags) {
      return;
    }
    let search = this.tagSearchControl.value;
    if (!search) {
      this.searchedTags.next(this.tags.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    this.searchedTags.next(
      this.tags.filter(tag => tag.name.toLowerCase().indexOf(search) > -1)
    );
  }

  clearFilter(filterType: string) {
    if (filterType === 'discipline') {
      this.selectedDisciplines = [];
      this.disciplineArray = [];
      this.disciplineControl.setValue(null);
    }
    if (filterType === 'location') {
      this.selectedLocations = [];
      this.locationArray = [];
      this.locationControl.setValue(null);
    }
    if (filterType === 'tag') {
      this.selectedTags = [];
      this.tagArray = [];
      this.tagControl.setValue(null);
    }
    if (filterType === 'registrationDateRange') {
      this.registrationDateRange = '';
      this.selectedRegistrationDateRange = '';
      this.registrationStartDate = '';
      this.registrationEndDate = '';
    }
    if (filterType === 'submissionDateRange') {
      this.submissionDateRange = '';
      this.selectedSubmissionDateRange = '';
      this.submissionStartDate = '';
      this.submissionEndDate = '';
    }
  }

  reset(sort: boolean = true) {
    if (sort) {
      this.selectedOrder = this.order[0].type;
    } else {
      this.selectedDisciplines = [];
      this.disciplineArray = [];
      this.disciplineControl.setValue(null);
      this.selectedLocations = [];
      this.locationArray = [];
      this.locationControl.setValue(null);
      this.selectedTags = [];
      this.tagArray = [];
      this.tagControl.setValue(null);
      this.isPrevious = this.isUpcoming = false;
      this.isAll = true;
      this.selectedShowType = this.show[0].type;
      this.selectedTimeline = this.timeline[0].type;
      this.selectedRegistrationDateRange = '';
      this.selectedSubmissionDateRange = '';
      this.registrationDateRange = '';
      this.submissionDateRange = '';
      this.registrationStartDate = '';
      this.registrationEndDate = '';
      this.submissionStartDate = '';
      this.submissionEndDate = '';
      this.organizationControl.setValue('');
      this.editorial = false;
    }
  }

  setDiscipline(discipline: Discipline) {
    if (!this.disciplineArray.includes(discipline.id)) {
      this.disciplineArray.push(discipline.id);
      this.selectedDisciplines.push(discipline);
    }
    this.disciplineControl.setValue(this.selectedDisciplines);
  }

  setLocation(location: Country) {
    if (!this.locationArray.includes(location.iso_code)) {
      this.selectedLocations.push(location);
      this.locationArray.push(location.iso_code);
    }
    this.locationControl.setValue(this.selectedLocations);
  }

  setTag(tag: Tag) {
    if (!this.tagArray.includes(tag.id)) {
      this.selectedTags.push(tag);
      this.tagArray.push(tag.id);
    }
    this.tagControl.setValue(this.selectedTags);
  }

  setDateRange(range: any) {
    if (range.placeholder == 'Submission date') {
      this.submissionDateRange = range.startDate + ',' + range.endDate;
      this.selectedSubmissionDateRange = range.selectedRange;
    }
    if (range.placeholder == 'Registration date') {
      this.registrationDateRange = range.startDate + ',' + range.endDate;
      this.selectedRegistrationDateRange = range.selectedRange;
    }
  }

  removeDiscipline(discipline: Discipline) {
    this.selectedDisciplines = this.selectedDisciplines.filter((discipline_obj, value) => {
      return discipline_obj.id !== discipline.id;
    });
    const index = this.disciplineArray.indexOf(discipline.id);
    if (index >= 0) {
      this.disciplineArray.splice(index, 1);
    }
    if (this.selectedDisciplines.length > 0) {
      this.disciplineControl.setValue(this.selectedDisciplines);
    } else {
      this.disciplineControl.setValue(null);
    }
  }

  removeLocation(location: Country) {
    this.selectedLocations = this.selectedLocations.filter((location_obj, value) => {
      return location_obj.iso_code !== location.iso_code;
    });
    const index = this.locationArray.indexOf(location.iso_code);
    if (index >= 0) {
      this.locationArray.splice(index, 1);
    }
    if (this.selectedLocations.length > 0) {
      this.locationControl.setValue(this.selectedLocations);
    } else {
      this.locationControl.setValue(null);
    }
  }

  removeRegistrationDateRange() {
    this.registrationDateRange = '';
    this.selectedRegistrationDateRange = '';
    this.registrationStartDate = '';
    this.registrationEndDate = '';
  }

  removeSubmissionDateRange() {
    this.submissionDateRange = '';
    this.selectedSubmissionDateRange = '';
    this.submissionStartDate = '';
    this.submissionEndDate = '';
  }

  removeTag(tag: Tag) {
    this.selectedTags = this.selectedTags.filter((tag_obj, value) => {
      return tag_obj.id !== tag.id;
    });
    const index = this.tagArray.indexOf(tag.id);
    if (index >= 0) {
      this.tagArray.splice(index, 1);
    }
    if (this.selectedTags.length > 0) {
      this.tagControl.setValue(this.selectedTags);
    } else {
      this.tagControl.setValue(null);
    }
  }

  resetPage() {
    // this.page = 1;
    // this.filteredResults = [];
  }

  toggleOrder(event) {
    this.selectedOrder = event.value;
  }

  toggleShow(event) {
    this.selectedShowType = event.value;
  }

  toggleTimeline(event) {
    this.selectedTimeline = event.value;
    if (this.selectedTimeline == 'previous') {
      this.isPrevious = true;
      this.isAll = this.isUpcoming = false;
    } else if (this.selectedTimeline == 'upcoming') {
      this.isUpcoming = true;
      this.isAll = this.isPrevious = false;
    } else if (this.selectedTimeline == 'all') {
      this.isAll = true;
      this.isPrevious = this.isUpcoming = false;
    } else if (this.selectedTimeline == 'ongoing') {
      this.isAll = this.isPrevious = this.isUpcoming = false;
    }
  }

  applyFilters() {
    const data = {
      selectedDisciplines: this.selectedDisciplines,
      selectedLocations: this.selectedLocations,
      selectedTags: this.selectedTags,
      selectedTimeline: this.selectedTimeline,
      selectedOrder: this.selectedOrder,
      selectedShowType : this.selectedShowType,
      registrationDateRange: this.registrationDateRange,
      submissionDateRange: this.submissionDateRange,
      selectedRegistrationDateRange: this.selectedRegistrationDateRange,
      selectedSubmissionDateRange: this.selectedSubmissionDateRange,
      registrationStartDate: this.registrationStartDate,
      registrationEndDate: this.registrationEndDate,
      submissionStartDate: this.submissionStartDate,
      submissionEndDate: this.submissionEndDate,
      organizationControl: this.organizationControl.value,
      isAll: this.isAll,
      isPrevious: this.isPrevious,
      isUpcoming: this.isUpcoming,
      disciplineArray: this.disciplineArray,
      locationArray: this.locationArray,
      tagArray: this.tagArray,
      editorial: this.editorial
    };
    this.dialogRef.close(data);
  }

  close() {
    this.dialogRef.close();
  }

}
